import {
  AlignLeft,
  Bold,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Highlighter,
  Info,
  Italic,
  List,
  Redo2,
  Strikethrough,
  Underline,
  Undo2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Activities from '../../../components/Activities/Activity';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';
import Header from '../../../components/Header';

import {
  downloadLeaveRoster,
  getLeaveRosterDetail,
  getOperationManagers,
  markLeaveRosterApproved,
  notifyLeaveRosterApproval,
  rejectLeaveRoster,
  updateLeave,
} from '../../../services/leaveRoasterService';

import { getShiftRosterGroups } from '../../../services/shiftRoasterService';

// ======================================================
// MONTHS
// ======================================================

const months = [
  { id: 1, name: 'JAN' },
  { id: 2, name: 'FEB' },
  { id: 3, name: 'MAR' },
  { id: 4, name: 'APR' },
  { id: 5, name: 'MAY' },
  { id: 6, name: 'JUN' },
  { id: 7, name: 'JUL' },
  { id: 8, name: 'AUG' },
  { id: 9, name: 'SEP' },
  { id: 10, name: 'OCT' },
  { id: 11, name: 'NOV' },
  { id: 12, name: 'DEC' },
];

// ======================================================
// INTERFACES
// ======================================================

interface LookupValue {
  id: number | string;
  title?: string;
  name?: string;
}

interface MonthlyLeave {
  month?: number | string;
  value?: number | string;
  leave_days?: number | string;
}

interface EmployeeDetail {
  id: number | string;
  user: LookupValue;
  shift_roster_group?: LookupValue | null;
  monthly_entitlement: string | number;
  accumulated_leave: string | number;
  total_balance: string | number;
  monthly_leaves: MonthlyLeave[];
  created_at: string;
  updated_at: string;
}

interface WorkflowApprover {
  id: number | string;
  user: LookupValue;
  status: string;
}

interface WorkflowStage {
  id: number | string;
  approvers: WorkflowApprover[];
}

interface LeaveRosterDetail {
  id: number | string;
  reference_number: string;
  year: number | string;
  start_month: number;
  end_month: number;
  creator: LookupValue;
  created_by?: number | string;
  status: LookupValue | string;
  details: EmployeeDetail[];
  workflow_stages: WorkflowStage[];
  created_at: string;
  updated_at: string;
}

interface ShiftRosterUser {
  id: number;
  user_id: number;
  user: {
    id: number;
    name: string;
    user_type?: { id: number; title: string };
  };
  is_active: number;
}

interface ShiftRosterGroup {
  id: number;
  title: string;
  department?: string;
  description?: string | null;
  is_active: number;
  users: ShiftRosterUser[];
}

interface OperationManager {
  id: number | string;
  name?: string;
  first_name?: string;
  last_name?: string;
}

// ======================================================
// LOCAL EDIT VALUE
// ======================================================

interface EmployeeLeaveValue {
  monthly_entitlement: string;
  accumulated_leave: string;
  total_balance: string;
  monthly_leaves: Record<number, string>;
}

// ======================================================
// COMPONENT
// ======================================================

export default function LeaveRosterDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ====================================================
  // CONFIGURATION NAVIGATION
  // ====================================================

  const handleClick = () => {
    navigate(`/LeaveRoster/${id}/groupmanagement`);
  };

  // ====================================================
  // STATES
  // ====================================================

  const [roster, setRoster] = useState<LeaveRosterDetail | null>(null);
  const [groups, setGroups] = useState<ShiftRosterGroup[]>([]);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ====================================================
  // EDIT VALUES
  // ====================================================

  const [leaveValues, setLeaveValues] = useState<Record<number, EmployeeLeaveValue>>({});

  // ====================================================
  // SAVING EMPLOYEE
  // ====================================================

  const [savingEmployee, setSavingEmployee] = useState<number | null>(null);

  // ====================================================
  // NOTIFY OPERATION MANAGER MODAL
  // ====================================================

  const [showNotifyApprovalModal, setShowNotifyApprovalModal] = useState(false);
  const [notifyDropdownOpen, setNotifyDropdownOpen] = useState(false);
  const [selectedOperationManager, setSelectedOperationManager] = useState<OperationManager | null>(null);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyError, setNotifyError] = useState<string | null>(null);
  const [operationManagers, setOperationManagers] = useState<OperationManager[]>([]);
  const [operationManagersLoading, setOperationManagersLoading] = useState(true);

  // ====================================================
  // APPROVE MODAL
  // ====================================================

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // ====================================================
  // REJECT MODAL
  // ====================================================

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  // ====================================================
  // ERROR MODAL
  // ====================================================

  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState({ title: '', message: '' });

  // ====================================================
  // SUCCESS TOAST
  // ====================================================

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // ====================================================
  // CURRENT USER
  // ====================================================

  const currentUserID = Number(localStorage.getItem('currentUserID'));

  // ====================================================
  // STATUS LABEL HELPER
  // (status can come back as a string or a LookupValue object)
  // ====================================================

  const getStatusLabel = (status: LookupValue | string | undefined): string => {
    if (!status) {
      return '-';
    }
    return typeof status === 'string' ? status : status.title || '-';
  };

  // ====================================================
  // EDIT PERMISSION
  // Only the Shift Supervisor can edit, and never once the
  // leave roster status is "Approved".
  // ====================================================

  const role = useSelector((state: any) => state.user.role);
  const isShiftSupervisor = role === 'shift_supervisor';
  const isApproved = getStatusLabel(roster?.status) === 'Approved';
  const canEditLeave = isShiftSupervisor && !isApproved;

  // ====================================================
  // DEBOUNCE TIMERS
  // ====================================================

  const leaveTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  // ====================================================
  // FORMAT LEAVE VALUE
  // (decimal -> integer, 0 / empty / null -> blank)
  // ====================================================

  const formatLeaveValue = (value: string | number | undefined | null): string => {
    if (value === undefined || value === null || value === '') {
      return '';
    }
    const num = Number(value);
    if (isNaN(num) || num === 0) {
      return '';
    }
    return String(Math.trunc(num));
  };

  // ====================================================
  // FETCH LEAVE ROSTER DETAILS
  // ====================================================

  const fetchLeaveRosterDetail = async () => {
    if (!id) {
      setError('Leave roster ID is missing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getLeaveRosterDetail(id);
      console.log('Leave roster detail response:', response);

      const data = response?.data?.data ?? response?.data ?? response;
      console.log('Leave roster detail:', data);

      setRoster(data);
    } catch (err: any) {
      console.error('Failed to fetch leave roster details:', err);
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load leave roster details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveRosterDetail();
  }, [id]);

  // ====================================================
  // FETCH SHIFT ROSTER GROUPS
  // ====================================================

  useEffect(() => {
    const fetchShiftRosterGroups = async () => {
      try {
        setGroupsLoading(true);

        const response = await getShiftRosterGroups();
        console.log('Shift roster groups response:', response);

        const data = response?.data?.data ?? response?.data ?? response;

        if (Array.isArray(data)) {
          const sortedGroups = [...data].sort((firstGroup, secondGroup) =>
            String(firstGroup?.title ?? '').localeCompare(String(secondGroup?.title ?? ''), undefined, {
              sensitivity: 'base',
              numeric: true,
            }),
          );

          setGroups(sortedGroups);
          setCurrentGroupIndex(0);
        } else {
          setGroups([]);
          setCurrentGroupIndex(0);
        }
      } catch (err) {
        console.error('Failed to fetch shift roster groups:', err);
        setGroups([]);
        setCurrentGroupIndex(0);
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchShiftRosterGroups();
  }, []);

  // ====================================================
  // FETCH OPERATION MANAGERS
  // ====================================================

  useEffect(() => {
    const fetchOperationManagers = async () => {
      try {
        setOperationManagersLoading(true);

        const response = await getOperationManagers();
        console.log('Operation managers response:', response);

        const data = response?.data?.data ?? response?.data ?? response;

        if (Array.isArray(data)) {
          setOperationManagers(data);
        } else {
          setOperationManagers([]);
        }
      } catch (err) {
        console.error('Failed to fetch operation managers:', err);
        setOperationManagers([]);
      } finally {
        setOperationManagersLoading(false);
      }
    };

    fetchOperationManagers();
  }, []);

  // ====================================================
  // CLEANUP TIMERS
  // ====================================================

  useEffect(() => {
    return () => {
      Object.values(leaveTimers.current).forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, []);

  // ====================================================
  // INITIALIZE EMPLOYEE VALUES
  // ====================================================

  useEffect(() => {
    if (!roster) {
      return;
    }

    const initialValues: Record<number, EmployeeLeaveValue> = {};

    roster.details?.forEach((detail) => {
      const userId = Number(detail.user?.id);
      if (!userId) {
        return;
      }

      const monthlyLeaves: Record<number, string> = {};

      detail.monthly_leaves?.forEach((leave) => {
        const month = Number(leave.month);
        if (month) {
          monthlyLeaves[month] = formatLeaveValue(leave.value ?? leave.leave_days);
        }
      });

      initialValues[userId] = {
        monthly_entitlement: formatLeaveValue(detail.monthly_entitlement),
        accumulated_leave: formatLeaveValue(detail.accumulated_leave),
        total_balance: formatLeaveValue(detail.total_balance),
        monthly_leaves: monthlyLeaves,
      };
    });

    setLeaveValues(initialValues);
  }, [roster]);

  // ====================================================
  // GET EMPLOYEE CURRENT VALUES
  // ====================================================

  const getEmployeeValues = (employee: ShiftRosterUser, detail?: EmployeeDetail): EmployeeLeaveValue => {
    const employeeId = Number(employee.user?.id);

    if (leaveValues[employeeId]) {
      return leaveValues[employeeId];
    }

    const monthlyLeaves: Record<number, string> = {};

    detail?.monthly_leaves?.forEach((leave) => {
      const month = Number(leave.month);
      if (month) {
        monthlyLeaves[month] = formatLeaveValue(leave.value ?? leave.leave_days);
      }
    });

    return {
      monthly_entitlement: formatLeaveValue(detail?.monthly_entitlement),
      accumulated_leave: formatLeaveValue(detail?.accumulated_leave),
      total_balance: formatLeaveValue(detail?.total_balance),
      monthly_leaves: monthlyLeaves,
    };
  };

  // ====================================================
  // UPDATE LEAVE API WITH DEBOUNCE
  // ====================================================

  const saveLeave = (employee: ShiftRosterUser, values: EmployeeLeaveValue) => {
    const employeeId = Number(employee.user?.id);

    if (leaveTimers.current[employeeId]) {
      clearTimeout(leaveTimers.current[employeeId]);
    }

    leaveTimers.current[employeeId] = setTimeout(async () => {
      try {
        setSavingEmployee(employeeId);

        const monthlyLeaves = Object.entries(values.monthly_leaves).map(([month, value]) => ({
          month: Number(month),
          leave_days: value,
        }));

        const payload = {
          leave_roster_id: Number(roster?.id),
          details: [
            {
              user_id: employeeId,
              shift_roster_group_id: Number(currentGroup?.id),
              monthly_entitlement: Number(values.monthly_entitlement) || 0,
              accumulated_leave: Number(values.accumulated_leave) || 0,
              total_balance: Number(values.total_balance) || 0,
              monthly_leaves: monthlyLeaves.map((item) => ({ ...item, leave_days: Number(item.leave_days) || 0 })),
            },
          ],
        };

        console.log('================================');
        console.log('UPDATE LEAVE PAYLOAD:', payload);
        console.log('================================');

        const response = await updateLeave(payload);
        console.log('Leave updated successfully:', response);
      } catch (error) {
        console.error('Failed to update leave:', error);
      } finally {
        setSavingEmployee(null);
      }
    }, 800);
  };

  // ====================================================
  // UPDATE EMPLOYEE FIELD
  // ====================================================

  const handleEmployeeValueChange = (
    employee: ShiftRosterUser,
    detail: EmployeeDetail | undefined,
    field: 'monthly_entitlement' | 'accumulated_leave' | 'total_balance',
    value: string,
  ) => {
    if (!canEditLeave) {
      return;
    }

    const employeeId = Number(employee.user?.id);
    const currentValues = getEmployeeValues(employee, detail);
    const updatedValues: EmployeeLeaveValue = { ...currentValues, [field]: value };

    setLeaveValues((prev) => ({ ...prev, [employeeId]: updatedValues }));

    saveLeave(employee, updatedValues);
  };

  // ====================================================
  // UPDATE MONTH VALUE
  // ====================================================

  const handleMonthChange = (employee: ShiftRosterUser, detail: EmployeeDetail | undefined, monthId: number, value: string) => {
    if (!canEditLeave) {
      return;
    }

    const employeeId = Number(employee.user?.id);
    const currentValues = getEmployeeValues(employee, detail);
    const updatedValues: EmployeeLeaveValue = {
      ...currentValues,
      monthly_leaves: { ...currentValues.monthly_leaves, [monthId]: value },
    };

    setLeaveValues((prev) => ({ ...prev, [employeeId]: updatedValues }));

    saveLeave(employee, updatedValues);
  };

  // ====================================================
  // OPERATION MANAGER DISPLAY NAME
  // ====================================================

  const getOperationManagerName = (manager: OperationManager): string => {
    if (manager.name) {
      return manager.name;
    }
    return [manager.first_name, manager.last_name].filter(Boolean).join(' ') || 'Unnamed';
  };

  // ====================================================
  // NOTIFY OPERATION MANAGER - SUBMIT
  // ====================================================

  const handleNotifyApproval = async () => {
    if (!selectedOperationManager || !roster) {
      return;
    }

    try {
      setNotifyLoading(true);
      setNotifyError(null);

      const payload = { user_ids: [Number(selectedOperationManager.id)] };
      console.log('Notify operation manager payload:', payload);

      const response = await notifyLeaveRosterApproval(Number(roster.id), payload);
      console.log('Notify operation manager response:', response);

      setShowNotifyApprovalModal(false);
      setSelectedOperationManager(null);

      await fetchLeaveRosterDetail();

      setSuccessMessage('Notification sent successfully.');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const response = error?.response?.data;
      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];
        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setNotifyError(message);
    } finally {
      setNotifyLoading(false);
    }
  };

  // ====================================================
  // APPROVE LEAVE ROSTER
  // ====================================================

  const handleApprove = async () => {
    if (!id) {
      return;
    }

    try {
      setApproveLoading(true);

      await markLeaveRosterApproved(id);

      setShowApproveModal(false);

      await fetchLeaveRosterDetail();

      setSuccessMessage('Leave roster approved successfully.');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const response = error?.response?.data;
      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];
        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({ title: 'Approval Failed', message });
      setErrorModal(true);
    } finally {
      setApproveLoading(false);
    }
  };

  // ====================================================
  // DOWNLOAD APPROVED LEAVE ROSTER
  // ====================================================

  const handleDownload = async () => {
    if (!id || !roster || !isApproved || downloadLoading) {
      return;
    }

    try {
      setDownloadLoading(true);

      const pdfBlob = await downloadLeaveRoster(id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `${roster.reference_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error: any) {
      let message = error?.message || 'Failed to download leave roster.';
      const responseData = error?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const parsedResponse = JSON.parse(await responseData.text());
          message = parsedResponse?.message || parsedResponse?.error || message;
        } catch {
          // Keep the fallback message when the Blob is not JSON.
        }
      } else {
        message = responseData?.message || responseData?.error || message;
      }

      setErrorMessage({ title: 'Download Failed', message });
      setErrorModal(true);
    } finally {
      setDownloadLoading(false);
    }
  };

  // ====================================================
  // REJECT LEAVE ROSTER
  // ====================================================

  const handleReject = async () => {
    if (!id) {
      return;
    }

    if (!rejectReason.trim()) {
      setErrorMessage({ title: 'Validation Error', message: 'Please provide a reason for rejection.' });
      setErrorModal(true);
      return;
    }

    try {
      setRejectLoading(true);

      await rejectLeaveRoster(id, rejectReason.trim());

      setShowRejectModal(false);
      setRejectReason('');

      await fetchLeaveRosterDetail();

      setSuccessMessage('Leave roster rejected successfully.');
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error: any) {
      const response = error?.response?.data;
      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];
        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({ title: 'Rejection Failed', message });
      setErrorModal(true);
    } finally {
      setRejectLoading(false);
    }
  };

  // ====================================================
  // LOADING
  // ====================================================

  if (loading) {
    return (
      <div className="bg-white min-h-screen">
        <Header title="Leave Roster Details" showBackButton />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-gray-500 text-sm">Loading leave roster details...</div>
        </div>
      </div>
    );
  }

  // ====================================================
  // ERROR
  // ====================================================

  if (error) {
    return (
      <div className="bg-white min-h-screen">
        <Header title="Leave Roster Details" showBackButton />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-red-500 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  // ====================================================
  // NO DATA
  // ====================================================

  if (!roster) {
    return (
      <div className="bg-white min-h-screen">
        <Header title="Leave Roster Details" showBackButton />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-gray-500 text-sm">Leave roster not found.</div>
        </div>
      </div>
    );
  }

  // ====================================================
  // DETAILS
  // ====================================================

  const details = roster.details ?? [];

  // ====================================================
  // START / END MONTH
  // ====================================================

  const startMonthNumber = Number(roster.start_month);
  const endMonthNumber = Number(roster.end_month);

  const startMonthName = months.find((month) => month.id === startMonthNumber)?.name ?? String(roster.start_month);
  const endMonthName = months.find((month) => month.id === endMonthNumber)?.name ?? String(roster.end_month);

  // ====================================================
  // ONLY START MONTH TO END MONTH
  // ====================================================

  const visibleMonths = months.filter((month) => month.id >= startMonthNumber && month.id <= endMonthNumber);

  // ====================================================
  // CURRENT GROUP
  // ====================================================

  const currentGroup = groups[currentGroupIndex];
  const currentGroupName = currentGroup?.title || 'Group';

  // ====================================================
  // CURRENT GROUP EMPLOYEES
  // ====================================================

  const currentGroupEmployees = currentGroup?.users ?? [];

  // ====================================================
  // INITIALS
  // ====================================================

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  // ====================================================
  // PREVIOUS GROUP
  // ====================================================

  const handlePreviousGroup = () => {
    if (groups.length === 0) {
      return;
    }
    setCurrentGroupIndex((prev) => {
      if (prev === 0) {
        return groups.length - 1;
      }
      return prev - 1;
    });
  };

  // ====================================================
  // NEXT GROUP
  // ====================================================

  const handleNextGroup = () => {
    if (groups.length === 0) {
      return;
    }
    setCurrentGroupIndex((prev) => {
      if (prev === groups.length - 1) {
        return 0;
      }
      return prev + 1;
    });
  };

  // ====================================================
  // UI
  // ====================================================

  return (
    <div className="bg-white min-h-screen">
      {/* HEADER */}
      <Header title="Leave Roster Details" showBackButton />

      <div className="p-6">
        <div className="grid grid-cols-12 gap-6">
          {/* LEFT CONTENT */}
          <div className="col-span-9">
            {/* TOP SUMMARY */}
            <div className="flex justify-between items-start mb-8">
              <div className="flex-1">
                {/* REFERENCE + STATUS */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl font-semibold text-[#0F172A]">{roster.reference_number}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#6C63FF]" />
                  <span className="text-[#6C63FF] text-sm font-medium">{getStatusLabel(roster.status)}</span>
                </div>

                {/* DATE */}
                <div className="flex items-center gap-2 text-[#94A3B8] text-sm mb-6">
                  <Calendar size={14} />
                  <span>{new Date(roster.created_at).toLocaleDateString('en-GB')}</span>
                </div>

                {/* TITLE */}
                <h2 className="text-xl font-semibold text-[#0F172A] mb-8">Annual Leave Program for {roster.year}</h2>

                {/* DETAILS */}
                <div className="grid grid-cols-3 gap-24">
                  {/* YEAR */}
                  <div>
                    <p className="text-[#94A3B8] text-sm mb-1">Year</p>
                    <p className="font-medium text-[#0F172A]">{roster.year}</p>
                  </div>

                  {/* START MONTH */}
                  <div>
                    <p className="text-[#94A3B8] text-sm mb-1">Start Month</p>
                    <p className="font-medium text-[#0F172A]">{startMonthName}</p>
                  </div>

                  {/* END MONTH */}
                  <div>
                    <p className="text-[#94A3B8] text-sm mb-1">End Month</p>
                    <p className="font-medium text-[#0F172A]">{endMonthName}</p>
                  </div>
                </div>
              </div>

              {/* Proceed / APPROVE / REJECT BUTTON */}
              <div className="ml-6 flex-shrink-0">
                {roster?.workflow_stages?.some((stage) =>
                  stage.approvers.some((approver) => Number(approver.user?.id) === currentUserID && approver.status === 'PENDING'),
                ) && getStatusLabel(roster?.status) === 'Approval Pending' ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowRejectModal(true);
                        setRejectReason('');
                      }}
                      className="px-4 py-2 border border-red-500 text-red-500 rounded-md"
                    >
                      Reject
                    </button>

                    <button onClick={() => setShowApproveModal(true)} className="px-4 py-2 bg-red-600 text-white rounded-md">
                      Approve
                    </button>
                  </div>
                ) : Number(roster?.creator?.id) === currentUserID && getStatusLabel(roster?.status) === 'New' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShowNotifyApprovalModal(true);
                      setNotifyError(null);
                    }}
                    className="px-6 py-2.5 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 transition-colors"
                  >
                    Proceed
                  </button>
                ) : Number(roster?.creator?.id) === currentUserID && getStatusLabel(roster?.status) === 'Approved' ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={downloadLoading}
                      className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed"
                    >
                      {downloadLoading ? 'Downloading...' : 'Download'}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            {/* LEAVE PROGRAM */}
            <div className="flex items-center gap-4 mb-4">
              <h3 className="text-base font-semibold text-[#0F172A]">
                Leave Program ({startMonthName} - {endMonthName})
              </h3>

              {/* GROUP NAVIGATION */}
              <div className="flex items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={handlePreviousGroup}
                  disabled={groupsLoading || groups.length === 0}
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-40"
                  aria-label="Previous group"
                >
                  <ChevronLeft size={18} />
                </button>

                <span className="font-semibold text-[#0F172A] min-w-[90px] text-center">
                  {groupsLoading ? 'Loading...' : currentGroupName}
                </span>

                <button
                  type="button"
                  onClick={handleNextGroup}
                  disabled={groupsLoading || groups.length === 0}
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-40"
                  aria-label="Next group"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* TABLE */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  {/* TABLE HEADER */}
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border px-4 py-3 text-left text-[#64748B] font-medium">Group</th>
                      <th className="border px-4 py-3 text-left text-[#64748B] font-medium">Employee</th>
                      <th className="border px-4 py-3 text-left text-[#64748B] font-medium">Designation</th>
                      <th className="border px-4 py-3 text-center text-[#64748B] font-medium">
                        Monthly
                        <br />
                        Entitlement
                      </th>
                      <th className="border px-4 py-3 text-center text-[#64748B] font-medium">
                        Accum.
                        <br />
                        Leave
                      </th>
                      <th className="border px-4 py-3 text-center text-[#64748B] font-medium">
                        Total
                        <br />
                        Balance
                      </th>

                      {/* ONLY VISIBLE MONTHS */}
                      {visibleMonths.map((month) => (
                        <th key={month.id} className="border px-2 py-3 text-center min-w-[56px] text-[#64748B] font-medium">
                          {month.name}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  {/* TABLE BODY */}
                  <tbody>
                    {groupsLoading ? (
                      <tr>
                        <td colSpan={6 + visibleMonths.length} className="border px-4 py-8 text-center text-gray-500">
                          Loading groups...
                        </td>
                      </tr>
                    ) : currentGroupEmployees.length === 0 ? (
                      <tr>
                        <td colSpan={6 + visibleMonths.length} className="border px-4 py-8 text-center text-gray-500">
                          No employees found for {currentGroupName}.
                        </td>
                      </tr>
                    ) : (
                      currentGroupEmployees.map((employee, rowIndex) => {
                        const detail = details.find((item) => Number(item.user?.id) === Number(employee.user?.id));
                        const employeeId = Number(employee.user?.id);
                        const currentValues = getEmployeeValues(employee, detail);

                        return (
                          <tr key={employee.id}>
                            {/* GROUP */}
                            {rowIndex === 0 && (
                              <td className="border px-2 py-3 text-center align-middle bg-white" rowSpan={currentGroupEmployees.length}>
                                <span
                                  className="inline-block text-[#6C63FF] font-medium text-sm whitespace-nowrap"
                                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                                >
                                  {currentGroupName}
                                </span>
                              </td>
                            )}

                            {/* EMPLOYEE */}
                            <td className="border px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold flex items-center justify-center">
                                  {getInitials(employee.user?.name || '')}
                                </div>
                                <span className="font-medium text-[#0F172A]">{employee.user?.name || '-'}</span>
                              </div>
                            </td>

                            {/* DESIGNATION */}
                            <td className="border px-4 py-3 text-center text-[#0F172A]">{employee.user?.user_type?.title || '-'}</td>

                            {/* MONTHLY ENTITLEMENT */}
                            <td className="border px-2 py-3 text-center">
                              <input
                                type="text"
                                value={currentValues.monthly_entitlement}
                                onChange={(e) => handleEmployeeValueChange(employee, detail, 'monthly_entitlement', e.target.value)}
                                readOnly={!canEditLeave}
                                className={`w-full text-center bg-transparent border-none outline-none focus:outline-none focus:ring-0 ${
                                  !canEditLeave ? 'text-gray-400 cursor-not-allowed' : ''
                                }`}
                              />
                            </td>

                            {/* ACCUMULATED LEAVE */}
                            <td className="border px-2 py-3 text-center">
                              <input
                                type="text"
                                value={currentValues.accumulated_leave}
                                onChange={(e) => handleEmployeeValueChange(employee, detail, 'accumulated_leave', e.target.value)}
                                readOnly={!canEditLeave}
                                className={`w-full text-center bg-transparent border-none outline-none focus:outline-none focus:ring-0 ${
                                  !canEditLeave ? 'text-gray-400 cursor-not-allowed' : ''
                                }`}
                              />
                            </td>

                            {/* TOTAL BALANCE */}
                            <td className="border px-2 py-3 text-center">
                              <input
                                type="text"
                                value={currentValues.total_balance}
                                onChange={(e) => handleEmployeeValueChange(employee, detail, 'total_balance', e.target.value)}
                                readOnly={!canEditLeave}
                                className={`w-full text-center bg-transparent border-none outline-none focus:outline-none focus:ring-0 ${
                                  !canEditLeave ? 'text-gray-400 cursor-not-allowed' : ''
                                }`}
                              />
                            </td>

                            {/* MONTH VALUES */}
                            {visibleMonths.map((month) => (
                              <td key={month.id} className="border h-14 text-center bg-white">
                                <input
                                  type="text"
                                  value={currentValues.monthly_leaves[month.id] ?? ''}
                                  onChange={(e) => handleMonthChange(employee, detail, month.id, e.target.value)}
                                  readOnly={!canEditLeave}
                                  className={`w-full h-full text-center bg-transparent border-none outline-none focus:outline-none focus:ring-0 ${
                                    !canEditLeave ? 'text-gray-400 cursor-not-allowed' : ''
                                  }`}
                                />
                              </td>
                            ))}

                            {/* SAVING STATUS */}
                            {savingEmployee === employeeId && <td className="hidden">Saving...</td>}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ACTIVITIES */}
          <div className="col-span-3 border-l pl-6">
            <Activities moduleName="leave_rosters" moduleItemId={Number(id)} />
          </div>
        </div>
      </div>

      {/* NOTIFY OPERATION MANAGER MODAL */}
      {showNotifyApprovalModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[480px] rounded-xl shadow-xl p-6 relative">
            <button
              onClick={() => {
                setShowNotifyApprovalModal(false);
                setNotifyDropdownOpen(false);
                setSelectedOperationManager(null);
                setNotifyError(null);
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              ✕
            </button>

            <h2 className="text-lg font-semibold mb-5">Notify to Approve</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Operation Manager</label>

              <div className="relative">
                <button
                  onClick={() => setNotifyDropdownOpen(!notifyDropdownOpen)}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2.5 bg-white text-sm"
                >
                  {selectedOperationManager ? (
                    getOperationManagerName(selectedOperationManager)
                  ) : (
                    <span className="text-gray-400">Select Operation Manager</span>
                  )}
                  <span>▼</span>
                </button>

                {notifyDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                    {operationManagersLoading ? (
                      <div className="px-4 py-3 text-sm text-gray-500">Loading...</div>
                    ) : operationManagers.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">No operation managers found.</div>
                    ) : (
                      operationManagers.map((manager) => (
                        <button
                          key={manager.id}
                          onClick={() => {
                            setSelectedOperationManager(manager);
                            setNotifyDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50"
                        >
                          {getOperationManagerName(manager)}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* NOTIFY ERROR */}
            {notifyError && (
              <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{notifyError}</div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowNotifyApprovalModal(false);
                  setNotifyDropdownOpen(false);
                  setSelectedOperationManager(null);
                  setNotifyError(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleNotifyApproval}
                disabled={!selectedOperationManager || notifyLoading}
                className={`px-6 py-2 rounded-md text-white ${selectedOperationManager && !notifyLoading ? 'bg-red-500 hover:bg-red-600' : 'bg-red-300 cursor-not-allowed'}`}
              >
                {notifyLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* APPROVE CONFIRMATION MODAL */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[400px] p-6 relative shadow-lg">
            <button onClick={() => setShowApproveModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              ✕
            </button>

            <h3 className="text-lg font-semibold text-center mb-6">Are you sure you want to approve this leave roster?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowApproveModal(false)}
                disabled={approveLoading}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleApprove}
                disabled={approveLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {approveLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT REASON MODAL */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[560px] shadow-lg overflow-hidden">
            {/* HEADER */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4">
              <h3 className="text-base font-semibold text-[#16325C]">Rejection</h3>

              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            {/* BODY */}
            <div className="px-6 pb-6">
              <div className="flex items-center gap-1.5 mb-2">
                <label className="text-sm font-medium text-[#16325C]">Reason for rejection</label>
                <Info size={14} className="text-gray-400" />
              </div>

              <div className="border border-gray-200 rounded-md overflow-hidden">
                {/* TOOLBAR */}
                <div className="flex items-center gap-3 px-3 py-2 bg-[#F8FAFC] border-b border-gray-200">
                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <Undo2 size={15} />
                  </button>

                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <Redo2 size={15} />
                  </button>

                  <button type="button" className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800">
                    Normal
                    <ChevronDown size={13} />
                  </button>

                  <div className="w-px h-4 bg-gray-300" />

                  <button type="button" className="flex items-center gap-1 text-gray-500 hover:text-gray-700">
                    <AlignLeft size={15} />
                    <ChevronDown size={13} />
                  </button>

                  <button type="button" className="flex items-center gap-1 text-gray-700 hover:text-gray-900">
                    <span className="w-3.5 h-3.5 rounded-sm bg-[#16325C] inline-block" />
                    <ChevronDown size={13} />
                  </button>

                  <div className="w-px h-4 bg-gray-300" />

                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <Bold size={15} />
                  </button>

                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <Italic size={15} />
                  </button>

                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <Underline size={15} />
                  </button>

                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <Strikethrough size={15} />
                  </button>

                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <Highlighter size={15} />
                  </button>

                  <button type="button" className="flex items-center gap-1 text-gray-500 hover:text-gray-700">
                    <List size={15} />
                    <ChevronDown size={13} />
                  </button>
                </div>

                {/* TEXTAREA */}
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={5}
                  placeholder="Add description here"
                  className="w-full px-4 py-3 text-sm text-[#16325C] placeholder-gray-400 bg-[#F8FAFC] focus:outline-none resize-none"
                />
              </div>
            </div>

            {/* FOOTER */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                disabled={rejectLoading}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleReject}
                disabled={rejectLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {rejectLoading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ERROR MODAL */}
      <ErrorModal
        isOpen={errorModal}
        onClose={() => setErrorModal(false)}
        title={errorMessage.title}
        message={errorMessage.message}
        buttonText="OK"
        onButtonClick={() => setErrorModal(false)}
      />

      {/* SUCCESS TOAST */}
      {showSuccess && (
        <div className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-lg shadow-lg flex items-center gap-3 z-[9999]">
          <CheckCircle size={24} />
          <div>
            <p className="font-semibold">Success</p>
            <p>{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}
